/**
 * Rules Reference Page
 * Comprehensive reference for weapon and armor special features
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import rulesData from '../data/weapon-armor-rules.json';
import advancementsData from '../data/advancements.json';
import CombatActionsReference from '../components/combat/CombatActionsReference';
import { EquipmentRulesTab } from './RulesReference/components/EquipmentRulesTab';
import { TraitsTab } from './RulesReference/components/TraitsTab';
import { SkillsTab } from './RulesReference/components/SkillsTab';
import { TalentsTab } from './RulesReference/components/TalentsTab';
import { PsychicPowersTab } from './RulesReference/components/PsychicPowersTab';
import { CareerAdvancementTab } from './RulesReference/components/CareerAdvancementTab';
import { AlternativeCareerAdvancesTab } from './RulesReference/components/AlternativeCareerAdvancesTab';
import { CriticalDamageTab } from './RulesReference/components/CriticalDamageTab';
import { AltRanksTab } from './RulesReference/components/AltRanksTab';
import { useRulebookStore, useRulebookSelectors } from '../stores/rulebooks';

type RuleCategory = 'all' | 'weapon' | 'armor';
type FilterCategory = 'all' | string;
type Career = string;
type TabType =
  | 'equipment'
  | 'advancement'
  | 'traits'
  | 'skills'
  | 'talents'
  | 'psychic-powers'
  | 'alternative-career-advances'
  | 'combat-actions'
  | 'critical-damage'
  | 'alt-ranks';

const TAB_GROUPS: Array<{
  heading: string;
  tabs: Array<{ id: TabType; label: string; icon: string }>;
}> = [
  {
    heading: 'Core Reference',
    tabs: [
      { id: 'equipment', label: 'Equipment Rules', icon: '⚔️' },
      { id: 'combat-actions', label: 'Combat Actions', icon: '🗡️' },
      { id: 'critical-damage', label: 'Critical Damage', icon: '☠️' },
      { id: 'alt-ranks', label: 'Alt-Ranks', icon: '🎖️' }
    ]
  },
  {
    heading: 'Character Reference',
    tabs: [
      { id: 'traits', label: 'Traits', icon: '🎭' },
      { id: 'skills', label: 'Skills', icon: '📚' },
      { id: 'talents', label: 'Talents', icon: '⭐' },
      { id: 'psychic-powers', label: 'Psychic Powers', icon: '🔮' },
      { id: 'advancement', label: 'Career Advancement', icon: '📈' },
      {
        id: 'alternative-career-advances',
        label: 'Alternative Career Advances',
        icon: '🧭'
      }
    ]
  }
];

export const RulesReference: React.FC = () => {
  const { availableRulebooks, loadRulebooks } = useRulebookStore();
  const { enabledRulebooks, rulebooksBySettings } = useRulebookSelectors();
  const [activeTab, setActiveTab] = useState<TabType>('equipment');
  const [ruleCategory, setRuleCategory] = useState<RuleCategory>('all');
  const [filterCategory, setFilterCategory] = useState<FilterCategory>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCareer, setSelectedCareer] = useState<Career | 'all'>('all');
  const [selectedRulebookIds, setSelectedRulebookIds] = useState<string[]>([]);
  const [isRulebookMenuOpen, setIsRulebookMenuOpen] = useState(false);
  const initializedRulebooks = useRef(false);

  useEffect(() => {
    loadRulebooks();
  }, [loadRulebooks]);

  useEffect(() => {
    if (!initializedRulebooks.current && enabledRulebooks.length > 0) {
      setSelectedRulebookIds(enabledRulebooks.map((rulebook) => rulebook.id));
      initializedRulebooks.current = true;
    }
  }, [enabledRulebooks]);

  // Get all unique categories
  const allCategories = useMemo(() => {
    const categories = new Set<string>();

    if (ruleCategory === 'all' || ruleCategory === 'weapon') {
      Object.values(rulesData.weaponRules).forEach((rule) => {
        categories.add(rule.category);
      });
    }

    if (ruleCategory === 'all' || ruleCategory === 'armor') {
      Object.values(rulesData.armorRules).forEach((rule) => {
        categories.add(rule.category);
      });
    }

    return Array.from(categories).sort();
  }, [ruleCategory]);

  // Filter rules based on selections
  const filteredRules = useMemo(() => {
    const rules: Array<{
      name: string;
      description: string;
      category: string;
      conversation?: any; // To allow for older data if present
      type: 'weapon' | 'armor';
      triggers?: any[];
      effects?: any[];
      conditions?: any[];
    }> = [];

    if (ruleCategory === 'all' || ruleCategory === 'weapon') {
      Object.values(rulesData.weaponRules).forEach((rule) => {
        if (
          selectedRulebookIds.includes(rule.rulebook) &&
          (filterCategory === 'all' || rule.category === filterCategory) &&
          (searchTerm === '' ||
            rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            rule.description.toLowerCase().includes(searchTerm.toLowerCase()))
        ) {
          rules.push({ ...rule, type: 'weapon' });
        }
      });
    }

    if (ruleCategory === 'all' || ruleCategory === 'armor') {
      Object.values(rulesData.armorRules).forEach((rule) => {
        if (
          selectedRulebookIds.includes(rule.rulebook) &&
          (filterCategory === 'all' || rule.category === filterCategory) &&
          (searchTerm === '' ||
            rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            rule.description.toLowerCase().includes(searchTerm.toLowerCase()))
        ) {
          rules.push({ ...rule, type: 'armor' });
        }
      });
    }

    return rules.sort((a, b) => a.name.localeCompare(b.name));
  }, [ruleCategory, filterCategory, searchTerm, selectedRulebookIds]);

  const getCategoryColor = (category: string) => {
    const colorMap: Record<string, string> = {
      damage: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200',
      penetration: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200',
      combat: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200',
      reliability: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200',
      area: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200',
      usability: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200',
      special: 'bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-200',
      accuracy: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-200',
      'rate-of-fire': 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200',
      mobility: 'bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200',
      enhancement: 'bg-violet-100 dark:bg-violet-900/30 text-violet-800 dark:text-violet-200',
      sensors: 'bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-200',
      communication: 'bg-lime-100 dark:bg-lime-900/30 text-lime-800 dark:text-lime-200',
      protection: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200',
      quality: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200',
      stealth: 'bg-slate-100 dark:bg-slate-900/30 text-slate-800 dark:text-slate-200'
    };

    return colorMap[category] || 'bg-surface-hover text-ink';
  };

  // Career advancement data
  const careerAdvancementData = useMemo(() => {
    const careers = advancementsData.careers as Career[];
    const summary: Record<
      string,
      {
        ranks: Record<
          number,
          { total: number; skills: number; talents: number; characteristics: number }
        >;
      }
    > = {};

    interface Advancement {
      career: string;
      rank: number;
      category: string;
    }

    careers.forEach((career) => {
      summary[career] = { ranks: {} };
      for (let rank = 1; rank <= 8; rank++) {
        const advs = (advancementsData.advancements as Advancement[]).filter(
          (a) => a.career === career && a.rank === rank
        );
        if (advs.length > 0) {
          summary[career].ranks[rank] = {
            total: advs.length,
            skills: advs.filter((a) => a.category === 'skill').length,
            talents: advs.filter((a) => a.category === 'talent').length,
            characteristics: advs.filter((a) => a.category === 'characteristic').length
          };
        }
      }
    });

    return { careers, summary };
  }, []);

  const groupedRulebooks = useMemo(() => {
    return Object.entries(rulebooksBySettings)
      .map(([settingId, rulebooks]) => ({
        settingId,
        label: settingId
          .split('-')
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' '),
        rulebooks
      }))
      .filter((group) => group.rulebooks.length > 0);
  }, [rulebooksBySettings]);

  const toggleRulebook = (rulebookId: string) => {
    setSelectedRulebookIds((current) =>
      current.includes(rulebookId)
        ? current.filter((id) => id !== rulebookId)
        : [...current, rulebookId]
    );
  };

  const applySettingsRulebooks = () => {
    setSelectedRulebookIds(enabledRulebooks.map((rulebook) => rulebook.id));
  };

  const selectAllRulebooks = () => {
    setSelectedRulebookIds(availableRulebooks.map((rulebook) => rulebook.id));
  };

  const setSettingGroupSelection = (rulebookIds: string[], shouldSelect: boolean) => {
    setSelectedRulebookIds((current) => {
      if (shouldSelect) {
        return Array.from(new Set([...current, ...rulebookIds]));
      }
      return current.filter((id) => !rulebookIds.includes(id));
    });
  };

  const isSettingGroupFullySelected = (rulebookIds: string[]) =>
    rulebookIds.length > 0 && rulebookIds.every((id) => selectedRulebookIds.includes(id));

  const isSettingGroupPartiallySelected = (rulebookIds: string[]) => {
    const selectedCount = rulebookIds.filter((id) => selectedRulebookIds.includes(id)).length;
    return selectedCount > 0 && selectedCount < rulebookIds.length;
  };

  return (
    <div className="container mx-auto px-4 py-6 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-ink mb-2">Rules Reference</h1>
        <p className="text-ink-dim">
          Comprehensive reference for equipment rules, traits, skills, talents, psychic powers, and
          career advancement in Rogue Trader
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6 rounded-xl border border-line bg-surface p-4 shadow-sm">
        <div className="space-y-4">
          {TAB_GROUPS.map((group, groupIndex) => (
            <div key={group.heading}>
              <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-ink-faint">
                {group.heading}
              </div>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex flex-wrap gap-2">
                  {group.tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                        activeTab === tab.id
                          ? 'border-blue-600 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'border-line text-ink hover:border-line hover:bg-surface-hover/70'
                      }`}
                    >
                      <span className="mr-2" aria-hidden="true">
                        {tab.icon}
                      </span>
                      {tab.label}
                    </button>
                  ))}
                </div>

                {groupIndex === 0 && (
                  <div className="relative lg:min-w-[19rem]">
                    <button
                      type="button"
                      onClick={() => setIsRulebookMenuOpen((open) => !open)}
                      className="flex w-full items-center justify-between rounded-lg border border-line bg-surface-alt px-3 py-2 text-left text-sm font-medium text-ink transition-colors hover:border-line hover:bg-surface dark:bg-surface-alt/40"
                    >
                      <span>
                        Source Books
                        <span className="ml-2 text-xs font-normal text-ink-faint">
                          {selectedRulebookIds.length} selected
                        </span>
                      </span>
                      <span className="ml-3 text-ink-faint">{isRulebookMenuOpen ? '▲' : '▼'}</span>
                    </button>

                    {isRulebookMenuOpen && (
                      <div className="absolute right-0 z-20 mt-2 w-full rounded-xl border border-line bg-surface p-4 shadow-xl">
                        <div className="mb-3 flex items-center justify-between gap-2">
                          <div className="text-sm font-semibold text-ink">Rulebooks to show</div>
                          <div className="flex gap-2 text-xs">
                            <button
                              type="button"
                              onClick={applySettingsRulebooks}
                              className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              Use Settings
                            </button>
                            <button
                              type="button"
                              onClick={selectAllRulebooks}
                              className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                              Select All
                            </button>
                          </div>
                        </div>

                        <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
                          {groupedRulebooks.map((groupedRulebook) => (
                            <div key={groupedRulebook.settingId}>
                              <div className="mb-1.5 flex items-center justify-between gap-2">
                                <label className="flex cursor-pointer items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-faint">
                                  <input
                                    type="checkbox"
                                    checked={isSettingGroupFullySelected(
                                      groupedRulebook.rulebooks.map((rulebook) => rulebook.id)
                                    )}
                                    ref={(input) => {
                                      if (input) {
                                        input.indeterminate = isSettingGroupPartiallySelected(
                                          groupedRulebook.rulebooks.map((rulebook) => rulebook.id)
                                        );
                                      }
                                    }}
                                    onChange={(e) =>
                                      setSettingGroupSelection(
                                        groupedRulebook.rulebooks.map((rulebook) => rulebook.id),
                                        e.target.checked
                                      )
                                    }
                                    className="rounded border-line text-blue-600 focus:ring-blue-500"
                                  />
                                  <span>{groupedRulebook.label}</span>
                                </label>
                                <div className="flex gap-2 text-[11px] font-medium">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setSettingGroupSelection(
                                        groupedRulebook.rulebooks.map((rulebook) => rulebook.id),
                                        true
                                      )
                                    }
                                    className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                  >
                                    All
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setSettingGroupSelection(
                                        groupedRulebook.rulebooks.map((rulebook) => rulebook.id),
                                        false
                                      )
                                    }
                                    className="text-ink-faint hover:text-ink"
                                  >
                                    None
                                  </button>
                                </div>
                              </div>
                              <div className="space-y-1">
                                {groupedRulebook.rulebooks.map((rulebook) => (
                                  <label
                                    key={rulebook.id}
                                    className="flex cursor-pointer items-start gap-2 rounded-md px-2 py-1 hover:bg-surface-hover dark:hover:bg-surface-hover/60"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedRulebookIds.includes(rulebook.id)}
                                      onChange={() => toggleRulebook(rulebook.id)}
                                      className="mt-0.5 rounded border-line text-blue-600 focus:ring-blue-500"
                                    />
                                    <div className="min-w-0">
                                      <div className="text-sm leading-5 font-medium text-ink">
                                        {rulebook.name}
                                      </div>
                                      {rulebook.description && (
                                        <div className="text-[11px] leading-4 text-ink-faint">
                                          {rulebook.description}
                                        </div>
                                      )}
                                    </div>
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'equipment' && (
        <EquipmentRulesTab
          ruleCategory={ruleCategory}
          setRuleCategory={setRuleCategory}
          filterCategory={filterCategory}
          setFilterCategory={setFilterCategory}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filteredRules={filteredRules}
          allCategories={allCategories}
          getCategoryColor={getCategoryColor}
        />
      )}
      {activeTab === 'traits' && (
        <TraitsTab
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedRulebookIds={selectedRulebookIds}
        />
      )}
      {activeTab === 'skills' && (
        <SkillsTab
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedRulebookIds={selectedRulebookIds}
        />
      )}
      {activeTab === 'talents' && (
        <TalentsTab
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedRulebookIds={selectedRulebookIds}
        />
      )}
      {activeTab === 'psychic-powers' && (
        <PsychicPowersTab
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedRulebookIds={selectedRulebookIds}
        />
      )}
      {activeTab === 'advancement' && (
        <CareerAdvancementTab
          selectedCareer={selectedCareer}
          setSelectedCareer={setSelectedCareer}
          careerAdvancementData={careerAdvancementData}
          selectedRulebookIds={selectedRulebookIds}
        />
      )}
      {activeTab === 'alternative-career-advances' && (
        <AlternativeCareerAdvancesTab
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedRulebookIds={selectedRulebookIds}
        />
      )}
      {activeTab === 'combat-actions' && (
        <div className="bg-surface border border-line rounded-lg p-6">
          <CombatActionsReference />
        </div>
      )}
      {activeTab === 'critical-damage' && <CriticalDamageTab />}
      {activeTab === 'alt-ranks' && (
        <AltRanksTab searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
      )}
    </div>
  );
};
